import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Slider,
  InputAdornment,
  CircularProgress,
  Alert,
  Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import debounce from 'lodash/debounce';

import { searchCompaniesByCity, checkWorkerPoolHealth, submitAnalysisTask, searchCities } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalNotification } from '../App';

const SearchForm = ({ onSearchResults, onSearchStart, onSearchError, onCreateAsyncTask, disabled = false }) => {
  const { user, loading: authLoading } = useAuth();
  const { showNotification, isSearching, startSearch, endSearch } = useGlobalNotification();
  const [city, setCity] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [cityOptions, setCityOptions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [orgCount, setOrgCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const withAnalysis = true; // Анализ всегда включен
  const [workerPoolStatus, setWorkerPoolStatus] = useState(null);
  const searchMode = 'auto'; // Всегда используем автоматический режим

  // Проверяем статус пула воркеров только один раз после авторизации
  const healthCheckDoneRef = useRef(false);
  
  useEffect(() => {
    // Не делаем запрос пока AuthContext загружается
    if (authLoading) {
      return;
    }

    // Если пользователь не авторизован, сбрасываем флаг health check
    if (!user) {
      healthCheckDoneRef.current = false;
      return;
    }

    // Проверяем только один раз за сессию
    if (healthCheckDoneRef.current) {
      return;
    }

    healthCheckDoneRef.current = true;

    const checkPoolHealth = async () => {
      try {
        const status = await checkWorkerPoolHealth();
        setWorkerPoolStatus(status);
      } catch (err) {
        // Игнорируем ошибки проверки worker pool - это не критично
        console.warn('Worker pool health check failed:', err);
      }
    };

    checkPoolHealth();
  }, [authLoading, user]); // Зависим от состояния авторизации

  const handleSliderChange = (event, newValue) => {
    setOrgCount(newValue);
  };

  const handleInputChange = (event) => {
    const value = Number(event.target.value);
    if (value >= 1 && value <= 100) {
      setOrgCount(value);
    }
  };

  // Debounced city search
  const fetchCities = useMemo(
    () =>
      debounce(async (request, callback) => {
        setCityLoading(true);
        try {
          const results = await searchCities(request);
          callback(results.items || []);
        } catch (error) {
          console.error('Error fetching cities:', error);
          callback([]);
        } finally {
          setCityLoading(false);
        }
      }, 300),
    [],
  );

  useEffect(() => {
    let active = true;

    if (inputValue === '') {
      setCityOptions(city ? [city] : []);
      return undefined;
    }

    fetchCities(inputValue, (results) => {
      if (active) {
        let newOptions = [];

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setCityOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [city, inputValue, fetchCities]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    // Если city - объект (выбран из списка), берем имя, иначе - саму строку (если пользователь ввел и не выбрал)
    const cityName = city ? (typeof city === 'string' ? city : city.name) : inputValue;

    if (!cityName || !cityName.trim()) {
      setError('Необходимо указать название города');
      if (onSearchError) onSearchError('Необходимо указать название города');
      return;
    }

    try {
      setLoading(true);
      startSearch(); // Блокируем навигацию
      if (onSearchStart) onSearchStart();

      const response = await searchCompaniesByCity(
        cityName.trim(),
        orgCount,
        null, // category
        withAnalysis
      );

      // Сохраняем реальный UUID от бэка для polling
      localStorage.setItem('current_task_id', response.task_id);
      localStorage.setItem('search_city', cityName.trim());

      if (onSearchResults) {
        onSearchResults({
          companies: [],
          total: 0,
          processingTime: 0,
          city: cityName.trim(),
          withAnalysis,
          isProcessing: true,
          message: 'Анализ выполняется. Пожалуйста, подождите...',
          timeout: false,
          task_id: response.task_id,  // Используем РЕАЛЬНЫЙ UUID от бэка
          orgCount: orgCount
        });
      }
      return;


    } catch (err) {
      console.error('SearchForm: Error creating task:', err);

      let errorMessage = 'Произошла ошибка при создании задачи анализа.';

      if (err.response?.status >= 500) {
        errorMessage = 'Сервер временно недоступен. Попробуйте позже.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Ошибка при создании задачи.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }

      // Используем глобальную систему для отображения ошибок
      showNotification(err, 'auto');

      // Показываем ошибку пользователю
      setError(errorMessage);
      if (onSearchError) onSearchError(errorMessage);

      // Очищаем состояние задачи при ошибке
      localStorage.removeItem('current_task_id');
    } finally {
      setLoading(false);
      endSearch(); // Разблокируем навигацию
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
            Поиск и анализ компаний
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          {workerPoolStatus?.data?.worker_pool_enabled
            ? `Расширенная система поиска активна. Можно одновременно анализировать множество компаний для получения быстрых результатов.`
            : 'Введите название города для поиска и анализа компаний. Система автоматически проанализирует каждую найденную компанию.'
          }
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                id="city-autocomplete"
                disableClearable
                options={cityOptions}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                filterOptions={(x) => x} // Disable built-in filtering to use server-side
                loading={cityLoading}
                value={city}
                onChange={(event, newValue) => {
                  setCity(newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Название города"
                    variant="outlined"
                    required
                    disabled={disabled || loading || isSearching}
                    placeholder="например: Екатеринбург, Москва, Санкт-Петербург"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationCityIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {cityLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.id || option.name}>
                      <Grid container alignItems="center">
                        <Grid item sx={{ display: 'flex', width: 44 }}>
                          <LocationCityIcon sx={{ color: 'text.secondary' }} />
                        </Grid>
                        <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                          <Box component="span" sx={{ fontWeight: 'bold' }}>
                            {option.name}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {option.region}
                          </Typography>
                        </Grid>
                      </Grid>
                    </li>
                  );
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography id="org-count-slider" gutterBottom>
                Количество организаций для поиска: {orgCount}
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs>
                  <Slider
                    value={orgCount}
                    onChange={handleSliderChange}
                    aria-labelledby="org-count-slider"
                    valueLabelDisplay="auto"
                    step={1}
                    marks
                    min={1}
                    max={100}
                    disabled={disabled || loading || isSearching}
                    sx={{
                      '& .MuiSlider-markLabel': {
                        fontSize: '0.75rem'
                      }
                    }}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    value={orgCount}
                    onChange={handleInputChange}
                    type="number"
                    disabled={disabled || loading || isSearching}
                    inputProps={{
                      step: 1,
                      min: 1,
                      max: 100,
                      'aria-labelledby': 'org-count-slider',
                    }}
                    sx={{ width: 80 }}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Grid>



            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={disabled || loading || isSearching}
                startIcon={(loading || isSearching) ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ py: 1.5 }}
              >
                {(disabled || loading || isSearching)
                  ? `Идет обработка...`
                  : `Найти и проанализировать компании`
                }
              </Button>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SearchForm;
